import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ALL_CHAINS, chainIdToPublicClient, getChainName, getExplorerAddressUrl } from "../../../anyspend/index.js";
import { GlareCard, Popover, PopoverContent, PopoverTrigger } from "../../../global-account/react/index.js";
import { cn } from "../../../shared/utils/index.js";
import { getIpfsUrl } from "../../../shared/utils/ipfs.js";
import { formatDisplayNumber, formatTokenAmount } from "../../../shared/utils/number.js";
import { MoreVertical } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { b3Viem } from "../../../shared/constants/chains/b3Viem.js";
import { AnySpendCustom } from "./AnySpendCustom.js";
// ABI for contractURI and uri functions
const CONTRACT_URI_ABI = [{
  inputs: [],
  name: "contractURI",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "uint256",
    name: "_tokenId",
    type: "uint256"
  }],
  name: "uri",
  outputs: [{
    internalType: "string",
    name: "",
    type: "string"
  }],
  stateMutability: "view",
  type: "function"
}];
export function AnySpendNFT({
  loadOrder,
  mode = "modal",
  recipientAddress,
  nftContract,
  onSuccess,
  onShowPointsDetail,
  senderAddress,
  slots,
  content,
  theme,
  showFiatOption = true
}) {
  const [imageUrlWithFallback, setFallbackImageUrl] = useState(nftContract.imageUrl);
  const hasFetchedRef = useRef(false);
  // Fetch contract metadata when imageUrl is empty
  useEffect(() => {
    async function fetchContractMetadata() {
      // fetch image Uri if not provided
      if (nftContract.imageUrl || hasFetchedRef.current) {
        return;
      }
      hasFetchedRef.current = true;
      try {
        // Use the chainIdToPublicClient utility function
        const publicClient = chainIdToPublicClient(nftContract.chainId);
        let metadataURI;
        // Use uri function if tokenId is available, otherwise use contractURI
        if (nftContract.tokenId !== null && nftContract.tokenId !== undefined) {
          console.log("Using uri function with tokenId:", nftContract.tokenId);
          metadataURI = await publicClient.readContract({
            address: nftContract.contractAddress,
            abi: CONTRACT_URI_ABI,
            functionName: "uri",
            args: [BigInt(nftContract.tokenId)]
          });
        } else {
          console.log("Using contractURI function");
          metadataURI = await publicClient.readContract({
            address: nftContract.contractAddress,
            abi: CONTRACT_URI_ABI,
            functionName: "contractURI"
          });
        }
        if (metadataURI) {
          // Fetch the metadata from IPFS
          const metadataUrl = getIpfsUrl(metadataURI);
          const response = await fetch(metadataUrl);
          const metadata = await response.json();
          if (metadata.image) {
            const fallbackUrl = getIpfsUrl(metadata.image);
            setFallbackImageUrl(fallbackUrl);
            console.log("fallbackImageUrl", fallbackUrl);
          }
        }
      } catch (error) {
        console.error("Error fetching contract metadata:", error);
      }
    }
    fetchContractMetadata();
  }, [nftContract.contractAddress, nftContract.chainId, nftContract.imageUrl, nftContract.tokenId]);
  const header = useCallback(({
    anyspendPrice,
    isLoadingAnyspendPrice
  }) => _jsxs(_Fragment, {
    children: [_jsxs("div", {
      className: "relative size-[200px]",
      children: [_jsx("div", {
        className: "absolute inset-0 scale-95 bg-black/30 blur-md"
      }), _jsxs(GlareCard, {
        className: "overflow-hidden",
        children: [imageUrlWithFallback && _jsx("img", {
          src: imageUrlWithFallback,
          alt: nftContract.name,
          className: "size-full object-cover"
        }), _jsx("div", {
          className: "absolute inset-0 rounded-xl border border-white/10"
        })]
      }), _jsx(DropdownMenu, {
        nftContract: nftContract
      })]
    }), _jsxs("div", {
      className: "from-b3-react-background to-as-on-surface-1 -mb-5 mt-[-100px] w-full rounded-t-lg bg-gradient-to-t",
      children: [_jsx("div", {
        className: "h-[100px] w-full"
      }), _jsxs("div", {
        className: "mb-1 flex w-full flex-col items-center gap-2 p-5",
        children: [_jsx("span", {
          className: "font-sf-rounded text-2xl font-semibold",
          children: nftContract.name
        }), _jsx("div", {
          className: "flex w-fit items-center gap-1",
          children: anyspendPrice ? _jsx(AnimatePresence, {
            mode: "wait",
            children: _jsx("div", {
              className: cn("text-as-primary group flex items-center text-3xl font-semibold transition-all", {
                "opacity-0": isLoadingAnyspendPrice
              }),
              children: formatDisplayNumber(anyspendPrice?.data?.currencyIn?.amountUsd, {
                style: "currency"
              })
            })
          }) : _jsx("div", {
            className: "h-[36px] w-full"
          })
        })]
      })]
    })]
  }), [imageUrlWithFallback, nftContract]);
  return _jsx(AnySpendCustom, {
    loadOrder: loadOrder,
    mode: mode,
    activeTab: "fiat",
    showFiatOption: showFiatOption,
    recipientAddress: recipientAddress,
    orderType: "mint_nft",
    dstChainId: nftContract.chainId,
    dstToken: nftContract.currency,
    dstAmount: nftContract.price,
    contractAddress: nftContract.contractAddress,
    encodedData: "0x",
    metadata: {
      type: "mint_nft",
      nftContract: nftContract
    },
    header: header,
    onSuccess: onSuccess,
    onShowPointsDetail: onShowPointsDetail,
    senderAddress: senderAddress,
    slots: slots,
    content: content,
    theme: theme
  });
}
function DropdownMenu(t0) {
  const $ = _c(8);
  const {
    nftContract
  } = t0;
  const [open, setOpen] = useState(false);
  const chain = ALL_CHAINS[nftContract.chainId];
  let t1;
  if ($[0] !== chain || $[1] !== nftContract.chainId || $[2] !== nftContract.contractAddress || $[3] !== nftContract.currency.decimals || $[4] !== nftContract.currency.symbol || $[5] !== nftContract.price || $[6] !== open) {
    const nftUrl = getExplorerAddressUrl(nftContract.chainId, nftContract.contractAddress);
    t1 = _jsxs(Popover, {
      open,
      onOpenChange: setOpen,
      children: [_jsx(PopoverTrigger, {
        asChild: true,
        children: _jsx("button", {
          className: "absolute bottom-3 right-3 z-10 flex items-center justify-center rounded-full bg-black/20 p-1 text-white hover:bg-black/30",
          "aria-label": "Open NFT menu",
          children: _jsx(MoreVertical, {
            className: "h-3 w-3"
          })
        })
      }), _jsx(PopoverContent, {
        align: "end",
        className: "bg-b3-react-background border-b3-react-border min-w-48 rounded-lg border p-0 shadow-md backdrop-blur-sm",
        children: _jsxs("div", {
          className: "pointer-events-auto flex w-full flex-col gap-2 py-2",
          children: [_jsxs("div", {
            className: "hover:bg-as-on-surface-3 flex cursor-default items-center gap-2 rounded px-2 text-sm",
            children: ["Native mint price:", _jsxs("span", {
              className: "font-semibold",
              children: [formatTokenAmount(BigInt(nftContract.price), nftContract.currency.decimals, 6, false), " ", nftContract.currency.symbol]
            })]
          }), _jsxs("div", {
            className: "hover:bg-as-on-surface-3 flex cursor-default items-center gap-2 rounded px-2",
            children: [_jsx("img", {
              src: chain?.logoUrl,
              className: cn("h-5 w-5", nftContract.chainId !== b3Viem.id && "rounded-full")
            }), _jsxs("span", {
              className: "text-sm",
              children: ["Minted on ", _jsx("span", {
                className: "font-semibold",
                children: chain ? getChainName(nftContract.chainId) : "Unknown"
              })]
            })]
          }), _jsx("a", {
            href: nftUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "hover:bg-as-on-surface-3 text-as-primary flex items-center gap-2 rounded px-2 text-sm",
            children: "View NFT onchain"
          })]
        })
      })]
    });
    $[0] = chain;
    $[1] = nftContract.chainId;
    $[2] = nftContract.contractAddress;
    $[3] = nftContract.currency.decimals;
    $[4] = nftContract.currency.symbol;
    $[5] = nftContract.price;
    $[6] = open;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}